import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Jaguar E-Type (Series 1 Fixed Head Coupe, ~1963) — the archetypal long-bonnet
// British sports coupe at true scale (1 unit = 1 metre): length ~4.46, width
// 1.66, height 1.22 (very low), wheelbase 2.44. Nose is +Z. The whole silhouette
// is ONE continuous low curve: an EXTREMELY long, rounded bonnet with a central
// power bulge sloping down to a low oval nose, a small greenhouse set far back
// (the front axle sits under the bonnet, the cabin over the rear), and a
// fastback glass roof sweeping in one unbroken slope down to a short rounded
// tail — no flat deck anywhere behind the cabin. The signature cues: the iconic
// oval "mouth" air intake (a chrome-rimmed horizontal oval with a thin vertical
// bar, no big grille), faired-in headlamps under glass covers on the rounded
// front wings, chrome wire wheels with knock-off spinners, slim split chrome
// bumpers, small round tail lamps and a hint of tan interior through the glass.
// Rounded masses are scaled spheres (blobs), never rotated, so the body reads
// voluptuous rather than slab-sided. Body panels carry the "paint" recolour slot
// (British Racing Green); `drive` spins the wire wheels and `hazards` blinks the
// four indicators.

const PAINT = "#1f4a2e"; // British Racing Green — the "paint" slot default
const CHROME = "#ccd1d6"; // bright chrome — bumpers, wire rims, oval mouth ring
const TRIM = "#17181b"; // gloss-black trim (pillars, valances, louvres)
const GRILLE = "#232529"; // dark interior of the oval mouth
const WELL = "#141518"; // wheel-well discs
const GLASS = "#20303a"; // tinted side / rear glazing
const SCREEN = "#33495c"; // windscreen (a shade lighter than the tint)
const CABIN = "#101216"; // near-black interior block behind the glass
const COVER = "#93b0c2"; // clear glass headlamp cover (faired lamps)
const TIRE = "#1a1b1d";
const SPOKE = "#c7ccd1"; // bright wire spokes / rim
const HUB = "#b7bcc2";
const LAMP = "#eef4f8"; // headlamp glass
const RED = "#a51525"; // tail lamps
const AMBER = "#e8a13a";
const INT = "#8a6b3f"; // tan interior hint through the glass
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The long front overhang (front axle far back
// under the bonnet, short rear overhang) is the E-Type's defining proportion.
const WHEEL_R = 0.33;
const TYRE_W = 0.185;
const WHEEL_X = 0.73; // half track — tyre outer face proud of the body side
const AXLE_F = 0.55; // front axle sits well back under the very long bonnet
const AXLE_R = -1.89; // wheelbase 2.44
const BODY_X = 0.74; // main body half-width
const OUTER = WHEEL_X + TYRE_W / 2; // tyre outer cap ~0.8225 (proud of the well)

// The CLOSED fastback roof: a low, narrow, body-colour skin that starts at the
// windscreen header and sweeps in ONE unbroken descending curve to the low tail
// (three overlapping tilted paint boxes, each top surface meeting the next).
// The rear window is only a modest pane lying on the upper slope, so green roof
// shows around it — the top reads as a solid coupe roof, never an open cavity.
// Breakpoints (z, y_top): (-0.49, 1.07) -> (-1.10, 1.00) -> (-1.60, 0.88)
// -> (-2.09, 0.73) -> the tail then rounds down.
const SEG1_ANGLE = 0.11; // cabin roof, nearly flat and low
const SEG2_ANGLE = 0.25; // upper fastback
const SEG3_ANGLE = 0.34; // lower fastback onto the tail
const BONNET_TILT = 0.105; // long bonnet, front lower toward the oval nose
const SCREEN_RAKE = 0.86; // deeply raked windscreen on the very low cabin

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the curvaceous bonnet, wings, haunches and tail cap. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One chrome wire wheel: dark tyre, a bright rim ring, twelve thin bright
 *  spokes proud of the tyre's outer cap, a chrome hub and a knock-off spinner
 *  cone. Every part shares `name` so the `drive` track spins them as one node
 *  about the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    {
      geometry: new THREE.TorusGeometry(0.29, 0.024, 6, 22),
      color: SPOKE,
      position: [side * (OUTER + 0.012), y, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      name,
    },
  ];
  for (let k = 0; k < 12; k++) {
    parts.push(
      box(0.016, 0.56, 0.016, SPOKE, [side * (OUTER + 0.008), y, z], [(k * Math.PI) / 12, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.075, 0.075, 0.05, 14, HUB, [side * (OUTER + 0.02), y, z], [0, 0, Math.PI / 2], { name }));
  parts.push({ // knock-off spinner cone pointing outward
    geometry: new THREE.ConeGeometry(0.05, 0.09, 12),
    color: CHROME,
    position: [side * (OUTER + 0.06), y, z] as Vec3,
    rotation: [0, 0, (-side * Math.PI) / 2] as Vec3,
    name,
  });
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus a slim
 *  body-coloured brow (an upper half-torus over the tyre). The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half; the tyre's outer cap stays
 *  proud of both so the wheel is never swallowed. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.4, 0.4, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.39, 0.03, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Fronts are amber chips under the faired headlamps; rears are amber chips
// beside the round tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.56, 0.5, 2.02], size: [0.1, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.56, 0.5, 2.02], size: [0.1, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.36, 0.53, -2.17], size: [0.08, 0.05, 0.04] },
  { name: "indicator-rr", pos: [0.36, 0.53, -2.17], size: [0.08, 0.05, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.4, 0.12, 4.0, TRIM, [0, 0.26, -0.03])); // underbody pan

  // -- Lower body (paint) -----------------------------------------------------
  // A full-length sill-to-waist tub, kept LOW so the bonnet and fastback boxes
  // are always the topmost surfaces (never a flat deck peeking above them).
  P.push(paint(1.46, 0.3, 4.0, [0, 0.47, -0.03])); // main tub, sill to waist
  // Green door/waist band under the greenhouse: fills the flanks up to the
  // beltline so the cabin sits on painted metal, not a black notch.
  P.push(paint(1.44, 0.26, 1.5, [0, 0.71, -0.3]));
  // Rounded rear haunches — the E-Type hip over the rear wheels.
  for (const s of S) P.push(blob([0.12, 0.22, 0.72], [s * 0.68, 0.55, -1.5]));
  // Soft lower front, bridging the tub to the narrow oval nose.
  P.push(blob([0.5, 0.18, 0.4], [0, 0.44, 1.92]));

  // -- The very long rounded bonnet (paint) -----------------------------------
  // Rounded front wings flanking a long bonnet core, all sloping down to the
  // low oval nose. The wings host the faired headlamps and are the widest point
  // up front.
  for (const s of S) P.push(blob([0.19, 0.16, 1.22], [s * 0.59, 0.6, 0.88]));
  P.push(paint(1.1, 0.18, 2.3, [0, 0.61, 0.95], [BONNET_TILT, 0, 0])); // long bonnet core
  P.push(blob([0.26, 0.11, 1.05], [0, 0.79, 1.02])); // central power bulge

  // -- Nose and tail caps (paint) ---------------------------------------------
  P.push(paint(1.0, 0.28, 0.22, [0, 0.55, 2.06])); // nose block behind the oval mouth
  // Short LOW tail that drops off the fastback (not a tall slab): a low panel
  // topped by a rounded cap where the descending roof lands.
  P.push(paint(1.34, 0.28, 0.2, [0, 0.53, -2.08]));
  P.push(blob([0.62, 0.09, 0.13], [0, 0.62, -2.09])); // rounded tail cap
  // Fill under the bumpers, back to the wells (no see-through in profile).
  P.push(box(1.2, 0.22, 0.28, TRIM, [0, 0.33, 2.0])); // front valance
  P.push(box(1.2, 0.22, 0.28, TRIM, [0, 0.32, -2.0])); // rear valance

  // -- The CLOSED low fastback roof: three overlapping body-colour boxes -------
  // One unbroken descending green skin from the windscreen header to the tail —
  // narrow and low over the cabin, widening onto the rear body. No open cavity,
  // no flat deck. The rear window is only a modest pane on the upper slope.
  P.push(paint(1.0, 0.13, 0.62, [0, 0.95, -0.8], [-SEG1_ANGLE, 0, 0])); // cabin roof skin (narrow, low)
  P.push(blob([0.49, 0.07, 0.34], [0, 0.98, -0.72])); // rounded roof crown
  P.push(paint(1.12, 0.26, 0.56, [0, 0.79, -1.34], [-SEG2_ANGLE, 0, 0])); // upper fastback
  P.push(paint(1.3, 0.34, 0.5, [0, 0.62, -1.84], [-SEG3_ANGLE, 0, 0])); // lower fastback onto the tail
  P.push(box(0.82, 0.03, 0.46, GLASS, [0, 0.945, -1.27], [-SEG2_ANGLE, 0, 0])); // modest rear window on the slope
  P.push(cyl(0.03, 0.03, 0.016, 12, CHROME, [0, 0.69, -2.0], [-SEG3_ANGLE, 0, 0])); // tail badge

  // -- Greenhouse: a low, narrow cabin bubble set well back -------------------
  // The green roof skin above caps it; side glass sits inboard of the roof so
  // nothing splays past it as "ears"; a dark interior block seals it.
  P.push(box(1.0, 0.2, 0.85, CABIN, [0, 0.8, -0.55])); // dark interior block sealing the cabin
  P.push(box(0.82, 0.05, 0.4, INT, [0, 0.77, -0.42])); // tan interior hint through the glass
  P.push(box(0.98, 0.32, 0.05, SCREEN, [0, 0.86, -0.34], [-SCREEN_RAKE, 0, 0])); // short raked windscreen
  for (const s of S) P.push(paint(0.08, 0.32, 0.08, [s * 0.44, 0.86, -0.34], [-SCREEN_RAKE, 0, 0])); // A-pillars
  for (const s of S) {
    P.push(box(0.03, 0.2, 0.72, GLASS, [s * 0.49, 0.85, -0.52], [0, 0, s * 0.12])); // low side glass, tumblehome in
    P.push(paint(0.05, 0.22, 0.07, [s * 0.48, 0.82, -0.86])); // C-pillar (painted, frames the glass)
    P.push(box(0.02, 0.024, 0.86, CHROME, [s * 0.495, 0.75, -0.5])); // chrome belt strip under the glass
  }

  // -- Front fascia: the iconic oval "mouth" + faired headlamps ---------------
  // A distinct horizontal dark OVAL (a flattened dark lens) with a bright chrome
  // rim and a thin vertical bar — the primary E-Type identity cue, low and
  // central on the nose.
  P.push({ geometry: new THREE.SphereGeometry(0.32, 18, 10), color: GRILLE, position: [0, 0.53, 2.11] as Vec3, scale: [1.15, 0.48, 0.14] as Vec3 }); // dark oval lens
  P.push({ // chrome oval rim — a torus scaled wide (faces +Z, no yaw, no shear)
    geometry: new THREE.TorusGeometry(0.34, 0.035, 8, 30),
    color: CHROME,
    position: [0, 0.53, 2.14] as Vec3,
    scale: [1.1, 0.46, 1] as Vec3,
  });
  P.push(box(0.03, 0.26, 0.05, CHROME, [0, 0.53, 2.16])); // thin vertical mouth bar
  for (const s of S) {
    // Faired headlamp on the rounded wing: chrome bezel, lamp glass, then a
    // clear glass cover flush with the wing (the Series 1 covered-lamp look).
    P.push({
      geometry: new THREE.CylinderGeometry(0.11, 0.11, 0.03, 18),
      color: CHROME, position: [s * 0.58, 0.64, 1.68] as Vec3,
      rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [1, 0.85, 1] as Vec3,
    }); // bezel
    P.push({
      geometry: new THREE.CylinderGeometry(0.09, 0.09, 0.02, 18),
      color: LAMP, position: [s * 0.58, 0.64, 1.695] as Vec3,
      rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [1, 0.85, 1] as Vec3,
    }); // lamp glass
    P.push({
      geometry: new THREE.SphereGeometry(0.11, 14, 10),
      color: COVER, position: [s * 0.58, 0.635, 1.71] as Vec3,
      scale: [1, 0.82, 0.4] as Vec3,
    }); // faired glass cover, flush on the wing front
  }
  // Slim split chrome bumpers wrapping each front corner.
  for (const s of S) {
    P.push(box(0.5, 0.06, 0.08, CHROME, [s * 0.42, 0.44, 2.13]));
    P.push(box(0.05, 0.14, 0.16, CHROME, [s * 0.66, 0.44, 2.04])); // corner wrap
  }
  P.push(box(0.34, 0.11, 0.02, PLATE, [0, 0.33, 2.17])); // front plate below the mouth

  // -- Rear fascia: small round tail lamps + split bumpers --------------------
  for (const s of S) {
    P.push(cyl(0.055, 0.055, 0.03, 14, CHROME, [s * 0.5, 0.55, -2.16], [Math.PI / 2, 0, 0])); // bezel
    P.push(cyl(0.04, 0.04, 0.04, 14, RED, [s * 0.5, 0.55, -2.18], [Math.PI / 2, 0, 0])); // small round red lens
  }
  for (const s of S) {
    P.push(box(0.5, 0.06, 0.08, CHROME, [s * 0.42, 0.4, -2.14]));
    P.push(box(0.05, 0.12, 0.16, CHROME, [s * 0.64, 0.42, -2.04])); // corner wrap
  }
  P.push(box(0.34, 0.1, 0.02, PLATE, [0, 0.35, -2.19])); // rear plate
  // Twin central chrome exhaust tips under the tail.
  for (const s of S) P.push(cyl(0.03, 0.03, 0.16, 12, CHROME, [s * 0.09, 0.26, -2.16], [Math.PI / 2, 0, 0]));

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.02, 0.02, TRIM, [s * 0.7, 0.8, -0.22])); // mirror arm, low on the door
    P.push(cyl(0.04, 0.04, 0.02, 12, CHROME, [s * 0.73, 0.81, -0.22], [0, 0, Math.PI / 2])); // mirror head
    P.push(box(0.02, 0.028, 0.1, CHROME, [s * 0.73, 0.62, -0.62])); // door handle
    P.push(box(0.24, 0.012, 0.03, TRIM, [s * 0.2, 0.77, -0.15], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const jaguarEType = defineModel({
  id: "jaguar-e-type",
  name: "Jaguar E-Type",
  batchId: "cars",
  tags: ["car", "jaguar", "e-type", "coupe", "sports", "classic"],
  summary: "A 1963 Jaguar E-Type Series 1 fixed-head coupe at true scale, in British Racing Green.",
  description:
    "A Jaguar E-Type Series 1 Fixed Head Coupe (~1963) at real 1 unit = 1 metre " +
    "scale (about 4.46 long, 1.66 wide, 1.22 tall, 2.44 wheelbase, on chrome " +
    "wire wheels). The whole silhouette is one continuous low curve: an " +
    "extremely long rounded bonnet with a central power bulge sloping to a low " +
    "oval nose, a small greenhouse set far back, and a fastback glass roof " +
    "sweeping in one unbroken line to a short rounded tail with no flat deck. " +
    "The signature cues are all present: the iconic chrome-rimmed oval mouth " +
    "intake with a thin vertical bar, faired-in headlamps under glass covers on " +
    "the rounded front wings, chrome wire wheels with knock-off spinners, slim " +
    "split chrome bumpers, small round tail lamps, twin central exhausts and a " +
    "tan interior hint through the glass. Body panels carry the 'paint' recolour " +
    "slot (British Racing Green); `drive` spins the wire wheels and `hazards` " +
    "blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.38,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
